import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Text,
  Table,
  Badge,
  Button,
  Select,
  Modal,
  Stack,
  Skeleton,
  Card,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEye } from '@tabler/icons-react';
import { useECommerceChannels, useECommerceOrders } from '@/hooks/usePortalApi';

const CHANNEL_COLORS: Record<string, string> = {
  shopify: 'green',
  woocommerce: 'purple',
  magento: 'orange',
  bigcommerce: 'blue',
  custom: 'gray',
};

const FULFILLMENT_COLORS: Record<string, string> = {
  pending: 'yellow',
  processing: 'blue',
  shipped: 'cyan',
  delivered: 'green',
  cancelled: 'red',
};

const fmt = (n: number) => '$' + n.toFixed(2);

export default function ECommerceOrders() {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);

  const { data: channels } = useECommerceChannels();
  const { data: orders, isLoading: ordersLoading } = useECommerceOrders(selectedChannel ? Number(selectedChannel) : undefined);

  const channelOptions = [
    { value: '', label: 'All Channels' },
    ...(channels || []).map((ch: any) => ({
      value: ch.id,
      label: ch.name,
    })),
  ];

  const handleViewDetails = (order: any) => {
    setSelectedOrder(order);
    openDetail();
  };

  const handleCloseDetail = () => {
    closeDetail();
    setSelectedOrder(null);
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>E-Commerce Orders</Title>
          <Text size="sm" c="dimmed">Orders from connected sales channels</Text>
        </div>
      </Group>

      <Group mb="md">
        <Select
          placeholder="Filter by channel"
          data={channelOptions}
          value={selectedChannel || ''}
          onChange={(value) => setSelectedChannel(value || null)}
          clearable
          style={{ width: 200 }}
        />
      </Group>

      {ordersLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !orders || orders.length === 0 ? (
        <Text ta="center" py="xl" c="dimmed">No e-commerce orders found</Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>External Order #</Table.Th>
              <Table.Th>Channel</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Items</Table.Th>
              <Table.Th>Total</Table.Th>
              <Table.Th>Fulfillment Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orders.map((order: any) => (
              <Table.Tr key={order.id}>
                <Table.Td>{order.external_order_number}</Table.Td>
                <Table.Td>
                  <Badge color={CHANNEL_COLORS[order.channel_type] || 'gray'} variant="light">
                    {order.channel_name}
                  </Badge>
                </Table.Td>
                <Table.Td>{new Date(order.order_date).toLocaleDateString()}</Table.Td>
                <Table.Td>{order.customer_name}</Table.Td>
                <Table.Td>{order.item_count}</Table.Td>
                <Table.Td>{fmt(order.total)}</Table.Td>
                <Table.Td>
                  <Badge color={FULFILLMENT_COLORS[order.fulfillment_status]} variant="light">
                    {order.fulfillment_status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEye size={14} />}
                    onClick={() => handleViewDetails(order)}
                  >
                    Details
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal
        opened={detailOpened}
        onClose={handleCloseDetail}
        title="Order Details"
        size="lg"
      >
        {selectedOrder && (
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={600}>External Order #</Text>
                  <Text>{selectedOrder.external_order_number}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Channel</Text>
                  <Badge color={CHANNEL_COLORS[selectedOrder.channel_type] || 'gray'} variant="light">
                    {selectedOrder.channel_name}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Order Date</Text>
                  <Text>{new Date(selectedOrder.order_date).toLocaleDateString()}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Customer</Text>
                  <Text>{selectedOrder.customer_name}</Text>
                </Group>
                <Group justify="space-between">
                  <Text fw={600}>Fulfillment Status</Text>
                  <Badge color={FULFILLMENT_COLORS[selectedOrder.fulfillment_status]} variant="light">
                    {selectedOrder.fulfillment_status}
                  </Badge>
                </Group>
              </Stack>
            </Card>

            <Divider label="Order Items" labelPosition="center" />

            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>SKU</Table.Th>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Qty</Table.Th>
                  <Table.Th>Price</Table.Th>
                  <Table.Th>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectedOrder.items?.map((item: any, idx: number) => (
                  <Table.Tr key={idx}>
                    <Table.Td>{item.sku}</Table.Td>
                    <Table.Td>{item.product_name}</Table.Td>
                    <Table.Td>{item.quantity}</Table.Td>
                    <Table.Td>{fmt(item.unit_price)}</Table.Td>
                    <Table.Td>{fmt(item.total)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            <Card withBorder>
              <Group justify="space-between">
                <Text fw={600} size="lg">Total</Text>
                <Text fw={600} size="lg">{fmt(selectedOrder.total)}</Text>
              </Group>
            </Card>

            {selectedOrder.tracking_number && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text fw={600}>Fulfillment Tracking</Text>
                  <Group justify="space-between">
                    <Text size="sm">Tracking Number</Text>
                    <Text size="sm">{selectedOrder.tracking_number}</Text>
                  </Group>
                  {selectedOrder.carrier && (
                    <Group justify="space-between">
                      <Text size="sm">Carrier</Text>
                      <Text size="sm">{selectedOrder.carrier}</Text>
                    </Group>
                  )}
                  {selectedOrder.shipped_date && (
                    <Group justify="space-between">
                      <Text size="sm">Shipped Date</Text>
                      <Text size="sm">{new Date(selectedOrder.shipped_date).toLocaleDateString()}</Text>
                    </Group>
                  )}
                </Stack>
              </Card>
            )}

            {selectedOrder.channel_specific_data && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text fw={600}>Channel-Specific Information</Text>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(selectedOrder.channel_specific_data, null, 2)}
                  </Text>
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
